import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, FileUp } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/marketing/components/ui/badge';
import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/marketing/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/marketing/components/ui/table';
import { cn } from '@/marketing/lib/utils';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import { parseVoucherCsv } from '@/marketing/lib/voucherCsvParser';
import { MAX_VOUCHER_BATCH_SIZE, type ImportVoucherRow } from '@/marketing/models/voucher';
import { VOUCHER_KEYS } from '@/marketing/infrastructure/influencer/voucher/utils/keys';
import { VOUCHER_MUTATIONS } from '@/marketing/infrastructure/influencer/voucher/utils/queries';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { VoucherBatchResultView, type VoucherBatchCount } from './VoucherBatchResultView';

type Step = 'upload' | 'preview' | 'result';

const PREVIEW_ROW_LIMIT = 10;
const PREVIEW_ERROR_LIMIT = 5;
const CSV_TEMPLATE = 'Handle,Platform,Commission,Coupon Code,Discount\ndevina,tiktok,8,GLOW_DEVINA,5\n';

interface ImportVouchersDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImportVouchersDialog({ open, onClose }: ImportVouchersDialogProps) {
  const queryClient = useQueryClient();
  const adminEmail = useAuthStore((state) => state.user)?.email ?? 'unknown';
  const [step, setStep] = useState<Step>('upload');
  const [rows, setRows] = useState<ImportVoucherRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [fallbackDiscount, setFallbackDiscount] = useState('5');
  const [isDragOver, setIsDragOver] = useState(false);
  const [counts, setCounts] = useState<VoucherBatchCount[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const importMutation = useMutation({
    ...VOUCHER_MUTATIONS.import(),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: VOUCHER_KEYS.all });
      setCounts([
        { label: 'Imported', value: result.imported, hint: 'attached to a creator' },
        { label: 'Unclaimed', value: result.unclaimed, hint: 'no Creator Hub account yet' },
        { label: 'Skipped', value: result.skipped, hint: 'already on file' },
        { label: 'Errors', value: result.errors.length, hint: 'listed below' },
      ]);
      setImportErrors(result.errors);
      setStep('result');
      toast.success(`Recorded ${result.imported + result.unclaimed} code(s)`);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to import codes'));
    },
  });

  const reset = () => {
    setStep('upload');
    setRows([]);
    setParseErrors([]);
    setCounts([]);
    setImportErrors([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'affiliate_code_import_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content !== 'string') {
        toast.error('Failed to read file content');
        return;
      }
      const parsed = parseVoucherCsv(content);
      setRows(parsed.rows);
      setParseErrors(parsed.errors);
      setStep('preview');
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      processFile(file);
      return;
    }
    toast.error('Please drop a .csv file');
  }, []);

  const discountPercent = Number(fallbackDiscount);
  const canImport =
    rows.length > 0 &&
    rows.length <= MAX_VOUCHER_BATCH_SIZE &&
    discountPercent >= 1 &&
    discountPercent <= 100 &&
    !importMutation.isPending;

  const runImport = () => {
    importMutation.mutate({ rows, importedBy: adminEmail, discountPercent });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className="sm:tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import affiliate codes</DialogTitle>
          <DialogDescription>
            Carry Upfluence codes over. Columns: handle, platform, commission, coupon code (optional), discount
            (optional). A row with no coupon code has one issued for it.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div
            className={cn(
              'tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-p-12 tw-transition-colors',
              isDragOver ? 'tw-border-blue-500 tw-bg-blue-50' : 'tw-border-muted',
            )}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <FileUp className="tw-mb-3 tw-h-10 tw-w-10 tw-text-muted-foreground" />
            <p className="tw-mb-1 tw-text-sm tw-font-medium">Drop a CSV file here, or click to browse</p>
            <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3">
              <label className="tw-cursor-pointer tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-primary-foreground hover:tw-bg-primary/90">
                Choose File
                <input
                  type="file"
                  accept=".csv"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      processFile(file);
                    }
                  }}
                  className="tw-hidden"
                />
              </label>
              <button
                type="button"
                onClick={downloadTemplate}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground"
              >
                <Download className="tw-h-3.5 tw-w-3.5" />
                Download template
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="tw-space-y-4">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Badge variant="secondary">{rows.length} rows parsed</Badge>
              {parseErrors.length > 0 && <Badge variant="destructive">{parseErrors.length} unusable rows</Badge>}
              <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-text-muted-foreground">Discount for rows without one</span>
                <Input
                  value={fallbackDiscount}
                  onChange={(event) => setFallbackDiscount(event.target.value.replace(/[^0-9]/g, ''))}
                  inputMode="numeric"
                  className="tw-w-[70px]"
                />
              </div>
            </div>

            {parseErrors.length > 0 && (
              <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-red-800">
                  <AlertCircle className="tw-h-4 tw-w-4" />
                  Rows that will not be sent
                </div>
                <ul className="tw-mt-1 tw-list-inside tw-list-disc tw-text-xs tw-text-red-700">
                  {parseErrors.slice(0, PREVIEW_ERROR_LIMIT).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                  {parseErrors.length > PREVIEW_ERROR_LIMIT && (
                    <li>...and {parseErrors.length - PREVIEW_ERROR_LIMIT} more</li>
                  )}
                </ul>
              </div>
            )}

            {rows.length > 0 && (
              <div className="tw-max-h-64 tw-overflow-auto tw-rounded-md tw-border">
                <Table>
                  <TableHeader>
                    <TableRow className="tw-bg-neutral-50">
                      <TableHead>Handle</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead className="tw-text-right">Commission</TableHead>
                      <TableHead>Coupon code</TableHead>
                      <TableHead className="tw-text-right">Discount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.slice(0, PREVIEW_ROW_LIMIT).map((row) => (
                      <TableRow key={`${row.platform}/${row.handle}`}>
                        <TableCell className="tw-text-sm">@{row.handle}</TableCell>
                        <TableCell className="tw-text-sm">{row.platform}</TableCell>
                        <TableCell className="tw-text-right tw-text-sm tw-tabular-nums">
                          {row.commissionPercent}%
                        </TableCell>
                        <TableCell className="tw-font-mono tw-text-xs">{row.couponCode ?? 'issue new'}</TableCell>
                        <TableCell className="tw-text-right tw-text-sm tw-tabular-nums">
                          {row.discountPercent ?? discountPercent}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {rows.length > PREVIEW_ROW_LIMIT && (
                  <p className="tw-px-3 tw-py-2 tw-text-xs tw-text-muted-foreground">
                    Showing {PREVIEW_ROW_LIMIT} of {rows.length} rows
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {step === 'result' && <VoucherBatchResultView counts={counts} errors={importErrors} />}

        <DialogFooter>
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={reset}>
                Back
              </Button>
              <Button onClick={runImport} disabled={!canImport}>
                {importMutation.isPending ? 'Importing…' : `Import ${rows.length} row(s)`}
              </Button>
            </>
          )}
          {step !== 'preview' && <Button onClick={close}>{step === 'result' ? 'Done' : 'Cancel'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
